import {
  KentRebalanceErrorStatus,
  KentRebalancingPlan,
  NormalizedRebalancingPlan,
  AutoscalerRebalancingPlan,
} from '@cast/types';

import {
  buildPlanOperationsFromKentProgress,
  mapKentErrorToOldError,
} from '../_utils/kentUtils';

export const mapAutoscalerRebalancingPlanToNormalizedRebalancingPlan = (
  plan: AutoscalerRebalancingPlan
): NormalizedRebalancingPlan => {
  // The API omits configurations when includeConfigurations is false, and operations when includeOperations is false.
  // Fall back to empty values so consumers never receive undefined.
  const fallbackConfigurations: AutoscalerRebalancingPlan['configurations'] =
    {};
  const fallbackOperations: AutoscalerRebalancingPlan['operations'] = [];

  return {
    rebalancingPlanId: plan.rebalancingPlanId,
    clusterId: plan.clusterId,
    scheduleId: plan.scheduleId ?? undefined,
    configurations: plan.configurations || fallbackConfigurations,
    operations: plan.operations || fallbackOperations,
    errors: plan.errors,
    rebalancingNodeIds: plan.rebalancingNodeIds,
    createdAt: plan.createdAt,
    updatedAt: plan.updatedAt,
    generatedAt: plan.generatedAt,
    triggeredAt: plan.triggeredAt,
    finishedAt: plan.finishedAt,
    variant: 'autoscaler',
    rawPlan: plan,
  };
};

export const mapKentRebalancingPlanToNormalizedRebalancingPlan = (
  kentPlan: KentRebalancingPlan
): NormalizedRebalancingPlan => {
  const fallbackDate = new Date().toISOString();

  return {
    rebalancingPlanId: kentPlan.id,
    clusterId: kentPlan.clusterId || '',
    scheduleId: kentPlan.scheduleName,
    configurations: {
      ...kentPlan.configurations,
      achieved: {
        clusterTotals: kentPlan.configurations?.achieved?.clusterTotals ?? {},
        affectedNodeTotals:
          kentPlan.configurations?.achieved?.affectedNodeTotals ?? {},
      },
    },
    operations: buildPlanOperationsFromKentProgress(kentPlan.executionProgress),
    errors: kentPlan.errors?.map((error) => ({
      pod: error.affectedResource,
      node: error.affectedResource,
      reason: mapKentErrorToOldError(
        error.code ??
          KentRebalanceErrorStatus.REBALANCING_ERROR_CODE_UNSPECIFIED
      ),
      message: error.message,
    })),
    rebalancingNodeIds: (kentPlan.rebalancingNodes || []).map(
      (node) => node.nodeName ?? ''
    ),
    createdAt: kentPlan.createTime || fallbackDate,
    updatedAt: kentPlan.updateTime || fallbackDate,
    generatedAt: kentPlan.createTime,
    triggeredAt: kentPlan.executeTime,
    finishedAt: kentPlan.completeTime,
    variant: 'kent',
    rawPlan: kentPlan,
  };
};
